import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { PortfolioDashboard } from '../../components/dashboard/PortfolioDashboard';
import { TradingViewWidget } from '../../components/charts/TradingViewWidget';
import { CryptoWatchlist } from '../../components/dashboard/CryptoWatchlist';
import { useAuth } from '../../contexts/AuthContext';
import { exchangeConnectorService } from '../../services/exchangeConnector';
import { portfolioDataService } from '../../services/portfolioDataService';

export const DashboardPage: React.FC = () => {
  const { state: { user } } = useAuth();
  const [loading, setLoading] = useState(true);
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (user) {
      const initializeAndLoadData = async () => {
        setLoading(true);
        try {
          // Initialize exchange connections
          await initializeExchangeConnections();
          
          // Preload portfolio data for both dashboard and portfolio page
          await preloadPortfolioData();
          
          // Then load dashboard data (just the total value)
          await loadDashboardData();
        } catch (error) {
          console.error('Error initializing connections and loading data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      initializeAndLoadData();
    }
  }, [user]);

  const initializeExchangeConnections = async () => {
    if (!user?.id) return;
    
    console.log('Initializing exchange connections from Dashboard...');
    try {
      // Initialize all connections through the exchange connector service
      const connectionResults = await exchangeConnectorService.initializeAllConnections(user.id);
      setConnectionStatus(connectionResults);
      console.log('Exchange connection results:', connectionResults);
    } catch (error) {
      console.error('Failed to initialize exchange connections:', error);
    }
  };
  
  // Preload portfolio data for both dashboard and portfolio page
  const preloadPortfolioData = async () => {
    if (!user?.id) return;
    
    console.log('Preloading portfolio data...');
    try {
      // Use the portfolio data service to load all exchange data
      await portfolioDataService.loadAllData(user.id);
      console.log('Portfolio data preloaded successfully');
    } catch (error) {
      console.error('Error preloading portfolio data:', error);
    }
  };

  const loadDashboardData = async () => {
    if (!user?.id) return;
    
    try {
      console.log('Loading dashboard data...');
      
      // Get the preloaded data from the portfolio data service
      const portfolioData = portfolioDataService.getData();
      
      // Calculate total portfolio value
      let totalValue = 0;
      Object.values(portfolioData).forEach(exchangeData => {
        if (exchangeData && typeof exchangeData.totalValue === 'number') {
          totalValue += exchangeData.totalValue;
        }
      });
      
      setTotalPortfolioValue(totalValue);
      console.log('Total portfolio value:', totalValue);
    } catch (error) {
      console.error('Error loading portfolio data:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PortfolioDashboard totalPortfolioValue={totalPortfolioValue} />
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Market Overview
                </Typography>
                <Box sx={{ height: 600 }}>
                  <TradingViewWidget 
                    theme="dark"
                    autosize
                    height={550}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <CryptoWatchlist />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardPage;
